import DOMPurify from 'dompurify'
import { mapGetters, mapActions } from 'vuex'
import { isEmpty, isEqual, differenceWith } from 'lodash'
import { useForm, defineRule } from 'vee-validate'
import { SubmissionObject, errors as submissionErrors } from 'src/pages/submissions/script/common.data'
import SubmissionComputed from 'src/pages/submissions/script/common.computed'
import SubmissionMethods from 'src/pages/submissions/script/common.methods'
import { TimeoutUtil } from 'src/utils/helper/timer'
import Misc from 'src/utils/helper/misc'

import Methods from './methods'
import Components from './components'
// import { globalValidators } from 'src/pages/submissions/script/common.data'

export default {
  name: 'prices',

  components: Components,

  setup() {
    const { errors, setErrors, validate } = useForm()
    defineRule('family_price_rule', (value, _, context) => {
      const fieldName = context.field
      if (!isEmpty(value)) {
        const isFamilyPackPriceLessThanSubPackagePrice = context.rule.params.some(
          price => parseFloat(price) > parseFloat(value)
        )
        if (isFamilyPackPriceLessThanSubPackagePrice) {
          return `The ${fieldName} is less than single / sub packages prices.`
        }
      }
      return true
    })
    defineRule('unique', (value, _, context) => {
      const fieldName = context.field
      const trimAndLowerCaseValue = Misc.dashedString(value).toLowerCase().trim()
      let uniqueNames = context.rule.params.map(objV => Misc.dashedString(objV))
      const trimAndLowerCaseUniqueNames = uniqueNames.map(name => {
        return name.toLowerCase().trim()
      })
      const _duplicates = Misc.getDuplicates(trimAndLowerCaseUniqueNames)
      if (_duplicates.indexOf(trimAndLowerCaseValue) >= 0) {
        return `The ${fieldName} should be unique`
      }
      return true
    })
    return {
      errors,
      setErrors,
      validate
    }
  },

  data() {
    return {
      packages: {
        familyPackage: {},
        singleStyles: [],
        customPackages: []
      },
      customSelectFontValue: 2,
      selectFont: {
        min: 1,
        accept: 2,
        max: 3
      },
      bannedWordForSubpackage: [],
      warningWordForSubpackage: [],
      warningWordListForSubpackage: [],
      currencies: {
        options: [
          {acronym: 'USD', symbol: '$'},
          {acronym: 'EUR', symbol: '€'},
          {acronym: 'GBP', symbol: '£'},
          {acronym: 'CAD', symbol: '$'},
          {acronym: 'AUD', symbol: '$'},
          {acronym: 'NZD', symbol: '$'},
          {acronym: 'JPY', symbol: '¥'},
          {acronym: 'BRL', symbol: 'R$'},
          {acronym: 'CNY', symbol: '¥'},
          {acronym: 'KRW', symbol: '₩'}
        ]
      },
      states: {
        conversions: 'unset',
        families: 'unset',
        singles: 'unset',
        custom: 'unset'
      },
      ...SubmissionObject,
      item: SubmissionObject.getStep('prices'),
      resetSyncTimer: TimeoutUtil(),
      subscribe: null,
      lastSingleStyle: null,
      singleStyleFamilyPack: null,
      selectedFont: null,
      selectedFontIndex: null,
      uploadFonts: '',
    }
  },

  watch: {
    errors: {
      handler(newVal) {
        if (!isEmpty(newVal)) {
          this.handleSubmissionErrors(newVal)
        } else {
          submissionErrors.value = submissionErrors.value.filter(error =>
            !Object.keys(error)[0].includes('currency style')
          )
        }
      },
      immediate: true
    },
    subPackageDoublesError: {
      handler(newVal) {
        if (newVal.length) {
          submissionErrors.value.push({
            'subpackage doubles prices': this.$t('pages.submissions.components.prices.custom.unique_content_error')
          })
        } else {
          this.removeErrorsNotPresent('subpackage doubles prices')
          this.removeErrorsNotPresent('subpackage_doubles')
        }
      },
      immediate: true
    },
    allMetaData: {
      handler(newVal,oldVal){
        this.uploadFonts = newVal
      }
    },
    submissionPackages: {
      handler: function (after, before) {
        // Watcher used for Setting Edit Data of Single Style Family package
        const isSingleStyleFamilyBefore = before.length === 1 && after.length > 1 && before[0].styles.length === 1 // Flag for Single Style Family Before
        const isSingleStyleFamilyAfterUpdate = this.submissionPimBinaries.length === 1 && after.length > 1 && before[0].styles.length > 1
        const isSingleStyleFamilyUpdated = before.length === 1 && after.length === 1 && (before[0].styles[0]?.binaryId !== this.submissionPimBinaries[0]?.binaryId) && !isEmpty(this.singleStyleFamilyPack) // Flag for Single Style Family Updated
        if (isSingleStyleFamilyUpdated) {
          after.forEach(data => {
            // update family pack
            if (data.packageType === 'familyPackage') {
              data.skuId = '0'
              data.oldSku = this.singleStyleFamilyPack.skuId
            }
          })
        }

        if (isSingleStyleFamilyBefore || isSingleStyleFamilyAfterUpdate) {
          const updatedSingleStyle = this.list.find(data => data.oldBinary)
          after.forEach(data => {
            // update family pack
            if (data.packageType === 'familyPackage') {
              data.skuId = '0'
              data.oldSku = '0'
            }
            // Add previous single style acting as single style family before package update
            if (
              data.packageType === 'singleStyle' &&
              data.styles[0].binaryId === this.submissionPimBinaries[0]?.binaryId
            ) {
              data.skuId = this.singleStyleFamilyPack.skuId
              data.oldSku = '0'
            }
            // Update previous single style acting as single style family before package update
            if (
              data.packageType === 'singleStyle' &&
              Misc.isValidValue(updatedSingleStyle) &&
              data.styles[0].binaryId === updatedSingleStyle.id
            ) {
              data.skuId = '0'
              data.oldSku = this.singleStyleFamilyPack.skuId
            }
          })
        }
        // Watcher used for Setting Edit Data of custom package
        const newCustomPack = after.filter(item => item.packageType === 'customPackage')
        const oldCustomPack = before.filter(item => item.packageType === 'customPackage')
        oldCustomPack.map(val => {
          delete val.workflow
        })
        if (oldCustomPack.length === newCustomPack.length) {
          // change in dropdown
          newCustomPack.forEach((pack, key) => {
            const isValid = pack.styles.length === oldCustomPack[key].styles.length
            if (!isValid) {
              if (pack.oldSku === '0') {
                pack.oldSku = pack.skuId
              }
              pack.skuId = 0
              oldCustomPack[key].styles.forEach((style, idx) => {
                const isExist = this.submissionBinaries.some(
                  item => item.hasOwnProperty('oldBinary') && item.oldBinary === style.binaryId
                )
                if (isExist) {
                  const index = Misc.findIndex(this.submissionBinaries, {
                    oldBinary: style.binaryId
                  })
                  this.packages.customPackages[key]?.select?.values.forEach(item => {
                    if (item.value === style.binaryId) {
                      item.value = this.submissionBinaries[index].binaryId
                      item.label = this.submissionBinaries[index].styleName
                      item.edit = this.submissionBinaries[index].styleName
                    }
                  })
                  const obj = {
                    binaryId: this.submissionBinaries[index].binaryId,
                    binaryName: this.submissionBinaries[index].styleName
                  }
                  pack.styles.push(obj)
                }
              })
            } else {
              pack.oldSku = oldCustomPack[key].oldSku
              pack.skuId = oldCustomPack[key].skuId
            }
          })
        }

        if (oldCustomPack.length < newCustomPack.length) {
          // Add Custom Pack
          const filteredCustomPack = this.setCustomPackWatcher2(newCustomPack, oldCustomPack, 'add')
          this.setCustomPackWatcher1(newCustomPack, filteredCustomPack)
        }

        if (oldCustomPack.length > newCustomPack.length) {
          // Remove Custom Pack
          const filteredCustomPack = this.setCustomPackWatcher2(newCustomPack, oldCustomPack, 'remove')
          this.setCustomPackWatcher1(newCustomPack, filteredCustomPack)
        }

        /** ********* Single Package data set ***********************
         * It sets submission packages global computed property
         * to set data for PUT API call and to make it consistent with others.
         * It sets packages.singleStyle local computed property
         * to show data on UI.
         */
        const newSinglePack = after.filter(item => item.packageType === 'singleStyle')
        const oldSinglePack = before.filter(item => item.packageType === 'singleStyle')
        oldSinglePack.map(val => {
          delete val.workflow
        })
        this.setSinglePackWatcher(newSinglePack, oldSinglePack)
      },
      deep: true
    }
  },

  created() {
    this.uploadFonts = false
    this.setSubmissionBuild({
      before: this.buildSubmission,
      after: this.setPackages
    })
    this.item.set({
      context: this
    })
    this.setSubscriptions()
    this.getCurrencyConversions().then(() => {
      this.states.conversions = 'set'
      this.$events.$emit('on_load_validate_sections', 'prices:currency')
      /**
       * Remove manually all the errors associated with currency style.
       * The watch property will then push the new errors to the global submissionErrors
       */
      const isCurrencyStyleError = (error) => {
        return Object.keys(error).some(err => err.includes('currency style'))
      }
      // Helper function to remove currency style errors
      function removeCurrencyStyleError(error) {
        submissionErrors.value = submissionErrors.value.filter(err => !Object.hasOwn(err, Object.keys(error)))
      }

      this.$events?.$on('delete:price', () => {
        submissionErrors.value.forEach(error => {
          if (isCurrencyStyleError(error)) {
            removeCurrencyStyleError(error)
          }
        })
        submissionErrors.value = [...new Set(submissionErrors.value.map(itm => JSON.stringify(itm)))].map(i =>
          JSON.parse(i)
        )
      })
    })

    /**
     * Add custom translation for min/max validation
     * in case of exactly 3 styles in submission
     */
    const translations = {}
    const maxCustom = 3
    for (let i = 0; i < maxCustom; i++) {
      translations[`subpackage_${i}`] = {
        min_value: this.$t('pages.submissions.components.prices.custom.styles_amount_2', { pack_no: i + 1 }),
        max_value: this.$t('pages.submissions.components.prices.custom.styles_amount_2', { pack_no: i + 1 })
      }
    }
    this.checkDoubles()

    // globalValidators.value.push(this.validate)

    this.$events?.$on('trigger:validate', () => {
      for (let key = this.packages.singleStyles.length - 1; key >= 0; key--) {
        const style = this.packages.singleStyles[key];
    
        for (let i = style.prices.length - 1; i >= 0; i--) {
          const id = this.$vId(this, `currency style ${key} ${i}`);
          
          //Skip if ID ends with "-0"
          if (id.endsWith("-0")) {
            continue;
          }
    
          const el = document.getElementById(id);
          if (el) {
            const value = el.value;
    
            if (value !== "" && value !== null && value !== undefined) {
              this.validateAndSavePrices({target: el}, key, i, this.currencies.options[i].acronym, this.packages.familyPackage.prices[i].price, this.packages.familyPackage.prices[0].price, true)
            }
          }
        }
      }
      this.validate()
    })
    this.handleCustomPackageValidation()
  },

  beforeUnmount() {
    this.unsubscribe()
    this.$events?.$off('trigger:validate')
    this.$events?.$off('delete:price')
    this.uploadFonts = ''
  },
  
  computed: {
    ...SubmissionComputed,
    ...mapGetters('binaries', ['getBinaryFontRenderSrc', 'list']),
    ...mapGetters('submissions', ['submission']),
    ...mapActions('binaries', ['getAll']),
    familyCurrencyError() {
      return this.filterErrorsBasedOnKeyProperty('family_currency')
    },
    priceDescription() {
      return DOMPurify.sanitize(this.$t('pages.submissions.components.prices.description'), {
        ADD_ATTR: ['target'],
      })
    },
    subPackageDoublesError() {
      return this.filterErrorsBasedOnKeyProperty('subpackage_doubles')
    },
    allMetaData () {
      return this.$store.getters['binaries/hasAllMeta']
    }
  },

  methods: {
    ...SubmissionMethods,
    ...Methods,
    formattedPrices() {
      const formatted = {};
      for (const [key, c] of Object.entries(this.packages.customPackages)) {
        formatted[key] = parseFloat(c.prices[0].price.toFixed(2));
      }
      return formatted;
    },
    removeSelectFontError() {
      this.packages.customPackages.forEach((item, index) => {
        if (!isEmpty(item.select.values)) {
          submissionErrors.value = submissionErrors.value.filter(
            error => !Object.hasOwn(error, `font sub family${index} prices`)
          )
        }
      })
    },
    saveCustomPrices(key, index, customKey, value) {
      if (value) {
        value = parseFloat(value).toFixed(2);
        this.formattedPrices[key] = value
      }
      submissionErrors.value = submissionErrors.value.filter(error => !Object.hasOwn(error, customKey))
      this.savePrices(key, index, customKey)
    },
    updateSubPackageName(name, key) {
      if (!isEmpty(name)) {
        submissionErrors.value = submissionErrors.value.filter(
          error => !Object.hasOwn(error, `currency custom${key}.custom package name${key} prices`)
        )
      }
      if(!this.bannedWordForSubpackage[key]) {
        this.commitChange()
      }
    },
    validateFontSelection(selected, index) {
      this.selectedFont = selected
      this.selectedFontIndex = index
      if(selected?.values?.length === 1 || (selected?.values?.length > selected?.options?.length - 1)) {
        submissionErrors.value.push({
          [`subpackage ${index} prices`]: this.validateSelectFontsLength(selected, index)
        })
      } else {
        this.removeErrorFromSubmission(`subpackage ${index} prices`)
      }
      if (!isEmpty(selected?.values)) {
        this.removeErrorFromSubmission(`font sub family${index} prices`)
      }
      this.checkDoubles()
    },

    handleSubmissionErrors(newVal) {
      this.removePricesErrorFromSubmission()
      if (!isEmpty(submissionErrors.value)) {
        submissionErrors.value = submissionErrors.value.filter(err => !Object.keys(err)[0].includes('currency style'))
        submissionErrors.value = submissionErrors.value.filter(err => !Object.keys(err)[0].includes('currency custom'))
      }
      this.removeCustomPackageErrors()
      for (const key in newVal) {
        submissionErrors.value.push({ [key]: newVal[key] })
      }
      this.removeSubPackageErrors()
      this.removeSelectFontError()
      submissionErrors.value = [...new Set(submissionErrors.value.map(itm => JSON.stringify(itm)))].map(i =>
        JSON.parse(i)
      )
    },
    /**
     * To remove the number of selected font that can be uploaded.
     * If the selected font total length is 3, we can only upload 2
     * If the selected font total length is 4, we can only between 2 and 3.
     * So, this.errors could not remove the manually added subpackage error from submissionError
     * We need to manually remove the errors if the font uploaded data is valid.
     */
    removeSubPackageErrors() {
      for (let i = 0; i < this.packages.customPackages.length; i++) {
        if (isEqual(this.packages.customPackages[i].select.options.length, this.selectFont.max)) {
          if (
            this.packages.customPackages[i].select.values?.length > this.selectFont.min &&
            this.packages.customPackages[i].select.values?.length < this.selectFont.max
          ) {
            submissionErrors.value = submissionErrors.value.filter(
              error => !Object.hasOwn(error, `subpackage ${i} prices`)
            )
          }
        } 
        else if (
          this.packages.customPackages[i].select.values?.length <=
            this.packages.customPackages[i].select.options.length - 1 &&
          this.packages.customPackages[i].select.values?.length >= 2
        ) {
          submissionErrors.value = submissionErrors.value.filter(
            error => !Object.hasOwn(error, `subpackage ${i} prices`)
          )
        }
      }
    },
    removeCustomPackageErrors() {
      if (!isEmpty(this.packages.customPackages)) {
        this.packages.customPackages.forEach((item, index) => {
          this.removeErrorsNotPresent(`currency custom${index}`)
          this.removeErrorsNotPresent(`external sku ${index} prices`)
        })
      } else {
        const keysToRemoveFromSubmission = [
          'currency custom0.custom package name0 prices',
          'external sku 0 prices',
          'font sub family0 prices'
        ]
        keysToRemoveFromSubmission.forEach(item => {
          this.removeErrorsNotPresent(item)
        })
      }
    },
    optionDeselected(option, selectedFont ,key) {
      selectedFont.values = selectedFont.values.filter(v => v.value !== option.value)
      selectedFont.state = 'set'
      selectedFont.updateCustom(selectedFont.values)
      if(selectedFont?.values?.length === 1 || (selectedFont?.values?.length > selectedFont?.options?.length - 1)) {
        submissionErrors.value.push({
          [`subpackage ${key} prices`]: this.validateSelectFontsLength(selectedFont, key)
        })
      } else {
        this.removeErrorFromSubmission(`subpackage ${key} prices`)
      }
      this.checkDoubles()
    },
    filterErrorsBasedOnKeyProperty(key) {
      return Object.keys(this.errors)
        .filter(error => error.includes(key))
        .map(error => this.errors[error])
    },
    validateSelectFontsLength(select, key) {
      const optionsLength = select.options.length;
      const isOptionsMaxLength = optionsLength === this.selectFont.max;
      let errorMessage;
      if (isOptionsMaxLength) {
        errorMessage = `The number of selected fonts for subpackage ${key+1} must be ${this.selectFont.accept}`;
      } else {
        errorMessage = `The number of selected fonts for subpackage ${key+1} must be between ${this.selectFont.accept} and ${optionsLength - 1}.`;
      }
      return errorMessage;
    },
    customSubPackageErrors(id) {
      return Object.keys(this.errors)
        .filter(error => error.includes(`currency custom${id}`))
        .map(error => this.errors[error])
    },
    setSinglePackWatcher(newSinglePack, oldSinglePack) {
      newSinglePack.forEach((newVal, key) => {
        if (
          newVal.hasOwnProperty('styles') &&
          oldSinglePack[key] !== undefined &&
          this.submissionBinaries.some(
            val => val.hasOwnProperty('oldBinary') && val.oldBinary === oldSinglePack[key].styles[0].binaryId
          )
        ) {
          if (newVal.styles[0].binaryId !== oldSinglePack[key].styles[0].binaryId) {
            newVal.prices = oldSinglePack[key].prices
            if (oldSinglePack[key].oldSku != '0') {
              newVal.oldSku = oldSinglePack[key].oldSku
            } else {
              newVal.oldSku = oldSinglePack[key].skuId
            }
            newVal.skuId = 0
            newVal.externalSku = oldSinglePack[key].externalSku
          }
        }
      })
    },
    setCustomPackWatcher1(newCustomPack, filteredCustomPack) {
      /***********
       * This watcher is to set custom pack skuId and old Sku
       * which is not bind with html fields, it sets skuId and old Sku
       * values by differing the data
       * for new custom pack and old custom pack in watch lifecycle
       */
      newCustomPack.forEach((pack, key) => {
        pack.skuId = filteredCustomPack[key]?.skuId
        pack.oldSku = filteredCustomPack[key]?.oldSku
      })
    },
    setCustomPackWatcher2(newCustomPack, oldCustomPack, status = 'add') {
      /***********
       * This watcher is to make a common data usage
       */
      const oP = Misc.getPackageDetails(oldCustomPack)
      const nP = Misc.getPackageDetails(newCustomPack)
      let filteredPack = []
      if (status === 'add') {
        const differedData = differenceWith(nP, oP, isEqual)[0]
        const index = Misc.findIndex(nP, differedData)
        filteredPack = [...oldCustomPack, newCustomPack[index]]
      }
      if (status === 'remove') {
        const differedData = differenceWith(oP, nP, isEqual)[0]
        const index = Misc.findIndex(oP, differedData)
        filteredPack = [...oldCustomPack]
        filteredPack.splice(index, 1)
      }
      return filteredPack
    },
    removeErrorFromSubmission(customKey) {
      submissionErrors.value = submissionErrors.value.filter(err => !err.hasOwnProperty(customKey))
    },
    removeErrorsNotPresent(customKey) {
      if (!Object.hasOwn(this.errors, customKey)) {
        submissionErrors.value = submissionErrors.value.filter(err => !err.hasOwnProperty(customKey))
      }
    }
  }
}
